<template>
  <transition name="exception-fade">
    <div
      v-if="exceptionWordsApi.state.visible"
      v-click-outside="closePopover"
      class="exception-popover-container"
      :class="['placement-' + exceptionWordsApi.state.placement]"
      :style="exceptionWordsApi.state.style"
      @mousedown.stop
      @mouseup.stop
    >
      <div class="exception-popover-content">
        <div class="exception-popover__title">Добавить в исключения?</div>

        <div class="exception-popover__body">
          <input
            v-model="exceptionWordsApi.state.text"
            class="exception-popover__input"
            :class="{ 'has-error': isLimitReached }"
            type="text"
            maxlength="30"
            @keyup.enter="addExceptionWord"
          />
          <div v-if="isLimitReached" class="exception-popover__limit-hint">
            Максимум 30 символов
          </div>
        </div>

        <div class="exception-popover__actions">
          <button type="button" class="btn-submit" @click="addExceptionWord">
            Добавить
          </button>
          <button type="button" class="btn-cancel" @click="closePopover">
            Отмена
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { generateTabFilters } from "@/utils/rsql";
import { and } from "rsql-builder";
import { createFilterData } from "@/utils/mapper";

export default {
  name: "TreeTableExceptionWordPopover",
  inject: ["exceptionWordsApi"],

  computed: {
    ...mapGetters("filterTabs", ["currentTab", "filterTabByParam"]),

    isLimitReached() {
      return (this.exceptionWordsApi.state.text?.length || 0) >= 30;
    },
  },

  methods: {
    ...mapActions("filterTabs", ["editFilterTab", "setCurrentTab"]),

    async addExceptionWord() {
      const text = this.exceptionWordsApi.state.text?.trim().slice(0, 30);

      if (!text || !this.currentTab) {
        return;
      }

      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }

      this.closePopover();

      try {
        const searchCriteria = this.currentTab.searchCriteria || {};

        const normalizeStringArray = (arr) => {
          if (!Array.isArray(arr)) return [];
          return arr
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                return item.value || item.name || item.text || "";
              }
              return item;
            })
            .filter(
              (val) =>
                val !== null && val !== undefined && String(val).trim() !== "",
            );
        };

        const rawExcludeWords =
          searchCriteria.excludeWords || this.currentTab.excludeWords || [];
        let currentExcludeWords = normalizeStringArray(rawExcludeWords);

        if (!currentExcludeWords.includes(text)) {
          currentExcludeWords.push(text);
        }

        const normalizedKeyWords = normalizeStringArray(
          searchCriteria.keyWords || this.currentTab.keyWords,
        );
        const normalizedRegions = normalizeStringArray(
          searchCriteria.regions || this.currentTab.regions,
        );
        const normalizedOrganizations = normalizeStringArray(
          searchCriteria.organizations || this.currentTab.organizations,
        );
        const normalizedCodesOKRB = normalizeStringArray(
          searchCriteria.codesOKRB || this.currentTab.codesOKRB,
        );

        const formData = {
          ...this.currentTab,
          ...searchCriteria,
          keyWords: normalizedKeyWords,
          excludeWords: currentExcludeWords,
          regions: normalizedRegions,
          organizations: normalizedOrganizations,
          codesOKRB: normalizedCodesOKRB,
        };

        const filterData = createFilterData(formData);

        if (!filterData.searchCriteria) {
          filterData.searchCriteria = {};
        }

        filterData.searchCriteria.excludeWords = currentExcludeWords;
        filterData.searchCriteria.keyWords = normalizedKeyWords;
        filterData.searchCriteria.regions = normalizedRegions;
        filterData.searchCriteria.organizations = normalizedOrganizations;
        filterData.searchCriteria.codesOKRB = normalizedCodesOKRB;

        const generatedFilters =
          generateTabFilters(filterData.searchCriteria) || [];
        const validFilters = generatedFilters.filter(Boolean);

        filterData.searchQuery = validFilters.length
          ? and(...validFilters)
          : "";

        await this.editFilterTab({
          filterId: this.currentTab.id,
          filterData,
        });

        const updatedTab = this.filterTabByParam("id", this.currentTab.id);
        if (updatedTab) {
          this.setCurrentTab(updatedTab);
        }
      } catch (e) {
        console.error("Не удалось добавить слово в исключения:", e);
      }
    },

    closePopover() {
      this.exceptionWordsApi.hide();
    },
  },
};
</script>

<style lang="less" scoped>
.exception-fade-enter-active,
.exception-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.exception-fade-enter,
.exception-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.exception-popover-container {
  z-index: 1000;
  transform-origin: top left;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
  }

  &::before {
    top: -10px;
    left: 9px;
    border-bottom: 10px solid #cfd8dd;
    border-right: 10px solid transparent;
    z-index: 1;
  }

  &::after {
    top: -6px;
    left: 11px;
    border-bottom: 8px solid #ffffff;
    border-right: 8px solid transparent;
    z-index: 2;
  }

  &.placement-top {
    transform-origin: bottom left;

    &::before {
      top: auto;
      bottom: -10px;
      border-bottom: none;
      border-top: 10px solid #cfd8dd;
    }

    &::after {
      top: auto;
      bottom: -6px;
      border-bottom: none;
      border-top: 8px solid #ffffff;
    }
  }
}

.exception-popover-content {
  width: 330px;
  height: fit-content;
  background-color: #ffffff;
  border-radius: 6px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  border: 2px solid #cfd8dd;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
}

.exception-popover__title {
  font-family: Roboto, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #37474f;
}

.exception-popover__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.exception-popover__input {
  box-sizing: border-box;
  width: 100%;
  height: 37px;
  padding: 0 8px;
  border: 1.5px solid #cfd8dd;
  border-radius: 4px;
  outline: none;
  font-size: 14px;
  color: #79919c;
}

.exception-popover__limit-hint {
  font-size: 12px;
  line-height: 14px;
  color: #fe2712;
  margin-top: 4px;
}

.exception-popover__actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: auto;

  button {
    height: 41px;
    width: 50%;
    padding: 0 16px;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    border: none;
    transition: background-color 0.2s;
  }

  .btn-cancel {
    background: transparent;
    color: #7e909b;
    border: 1px solid #c8d1d5;

    &:hover {
      background: #f4f6f8;
    }
  }

  .btn-submit {
    background: #7e909b;
    color: #fff;

    &:hover {
      background: #6d808b;
    }
  }
}
</style>
